import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { useVerifyEmailResendMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import { EmailVerificationResendFormValues } from "./types";

const emailVerificationResendSchema = (
  t: TFunction,
): yup.ObjectSchema<EmailVerificationResendFormValues> =>
  yup.object().shape({
    email: yup
      .string()
      .typeError(t("auth.validation.emailNotValid"))
      .email(t("auth.validation.enterValidEmail"))
      .required(t("auth.validation.enterValidEmail")),
  });

const EmailVerificationResend = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [verifyEmailResend, { isLoading }] = useVerifyEmailResendMutation();
  const { t } = useTranslation();

  const form = useForm<EmailVerificationResendFormValues>({
    resolver: yupResolver(emailVerificationResendSchema(t)),
    mode: "onChange",
  });

  const onSubmit = (values: EmailVerificationResendFormValues) => {
    verifyEmailResend(values)
      .unwrap()
      .then((data) => {
        notification.success({ message: data.message });
        navigate("/email-verification");
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <div className="flex flex-col gap-400">
      <h2 className="header-l-medium text-center">
        {t("auth.labels.resendEmailVerification")}
      </h2>

      <div className="form-input-container">
        <FormFieldWrapper label={t("auth.labels.email")} required>
          <HookFormInput name="email" control={form.control} />
        </FormFieldWrapper>

        <div className="flex flex-col items-center">
          <BaseButton
            disabled={isLoading}
            isFullWidth
            onClick={form.handleSubmit(onSubmit)}
          >
            {t("auth.labels.submit")}
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationResend;
